import { useState, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { X, Copy, Check, User, MapPin, TrendingUp, ChevronRight, ChevronDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import type { Player } from "../../../shared/schema";
import TournamentResultPN from "./tournament-result-pn";

// Vue Joueur (refonte Padel Now) — overlay plein écran au clic sur une ligne du classement.
// Overlay maison (pas Radix) : rendu/clic garantis, et fidèle à la fiche plein écran de Padel Now.
// ponytail: labels FR en clair pour cette 1re tranche ; i18n à câbler ensuite.

const ELO_TIERS = [1149, 1299, 1399, 1499, 1599, 1749, 1999];
const tierOf = (elo: number) => ELO_TIERS.findIndex((m) => elo <= m) + 1 || 8;

// Données FFT additionnelles passées depuis la liste FFT (âge, club non stockés sur le joueur PadELO).
type FftExtra = { idCrm?: string | null; prenom?: string | null; nom?: string | null; rank?: number | null; points?: number | null; evolution?: number | null; licence?: string | null; age?: number | string | null; club?: string | null } | null;

type Competition = { competition_id: number; nom_tournoi: string | null; niveau: string | null; categorie: string | null; partenaire: string | null; rang_obtenu: number | null; points: number | null; expiration: string | null };
const frDate = (d: string | null) => { if (!d) return "—"; const x = new Date(d); return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("fr-FR"); };
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
// categorie FFT = format du tableau. niveau = le niveau P/EQP (affiché comme "catégorie" dans Padel Now).
const formatLabel = (cat: string | null) => ({ DM: "Double Messieurs", DD: "Double Dames", DX: "Double Mixte", MX: "Double Mixte", SM: "Simple Messieurs", SD: "Simple Dames" } as Record<string, string>)[cat ?? ""] || cat || "";
const NIVEAU_ORDER = ["P25", "P50", "P100", "P250", "P500", "P1000", "P1500", "P2000", "EQP1", "EQP2", "EQP3", "EQP4", "EQP5", "EQP6", "EQP8", "EQP10", "EQP11"];
const niveauIdx = (n: string | null) => { const i = NIVEAU_ORDER.indexOf(n ?? ""); return i < 0 ? 999 : i; };
const ordinal = (r: number) => (r === 1 ? "1er" : `${r}e`);
const medalBg = (r: number) => (r === 1 ? "#F5C518" : r === 2 ? "#C0C0C0" : "#CD7F32");

type Partner = { name: string; n: number; prenom: string | null; nom: string | null; rank: number | string | null };
// Mois restants avant expiration FFT (règle +1 mois : le tournoi reste valide le mois d'expiration).
const monthsUntil = (d: string | null): number | null => {
  if (!d) return null;
  const eff = new Date(d); if (isNaN(eff.getTime())) return null;
  eff.setMonth(eff.getMonth() + 1);
  return Math.round((eff.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44));
};

const FlagFR = () => (
  <span className="inline-flex w-[18px] h-[13px] rounded-sm overflow-hidden border border-black/10 shrink-0">
    <i className="flex-1" style={{ background: "#0055A4" }} /><i className="flex-1 bg-white" /><i className="flex-1" style={{ background: "#EF4135" }} />
  </span>
);

function EloChart({ data }: { data: { eloRating: number }[] }) {
  const { t } = useTranslation();
  if (!data?.length) return <p className="text-center text-muted-foreground py-6">{t("ranking.stats.noHistory", "Pas encore d'historique")}</p>;
  const pts = [...data].reverse().map((d) => d.eloRating);
  const min = Math.min(...pts), max = Math.max(...pts), range = max - min || 100;
  const W = 320, H = 140, pad = 16;
  const x = (i: number) => pad + (i * (W - pad * 2)) / (pts.length - 1 || 1);
  const y = (v: number) => pad + (H - pad * 2) - ((v - min) / range) * (H - pad * 2);
  const line = pts.map((v, i) => (i ? "L" : "M") + x(i).toFixed(1) + " " + y(v).toFixed(1)).join(" ");
  const area = `${line} L ${x(pts.length - 1).toFixed(1)} ${H - pad} L ${pad} ${H - pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <path d={area} fill="var(--pn-blue)" opacity="0.08" />
      <path d={line} fill="none" stroke="var(--pn-blue)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="3" fill="var(--pn-blue)" />)}
    </svg>
  );
}

export default function PlayerSheetPN({ playerId, open, onClose, fft }: { playerId: string | null; open: boolean; onClose: () => void; fft?: FftExtra }) {
  const { data: player, isLoading } = useQuery<Player>({
    queryKey: ["/api/players", playerId],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}`)).json(),
    enabled: !!playerId && open,
  });
  const { data: history = [] } = useQuery<{ eloRating: number }[]>({
    queryKey: ["/api/players", playerId, "elo-history", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/elo-history?limit=50&timeFilter=all`)).json(),
    enabled: !!playerId && open,
  });

  const [view, setView] = useState<"padelo" | "fft">("padelo");
  const [copied, setCopied] = useState(false);
  const [tri, setTri] = useState<"points" | "date" | "rang">("points");
  const [triOpen, setTriOpen] = useState(false);
  const [openComp, setOpenComp] = useState<number | null>(null);
  const [showUncounted, setShowUncounted] = useState(false);
  // Ouvert depuis la recherche FFT (idCrm fourni) → fiche FFT par défaut ; sinon PadELO.
  useEffect(() => { setView(fft?.idCrm ? "fft" : "padelo"); setCopied(false); setTri("points"); setOpenComp(null); setShowUncounted(false); }, [playerId, fft?.idCrm]);
  const copyLicence = (lic: string) => {
    try { navigator.clipboard.writeText(lic); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard indispo */ }
  };

  // Ouvert depuis la liste PadELO : pas d'idCrm, seulement la licence → on résout l'idCrm pour la fiche FFT complète.
  const { data: crm } = useQuery<{ idCrm: string | null; rank?: number | null; points?: number | null; evolution?: number | null; age?: number | null; club?: string | null }>({
    queryKey: ["/api/players", "fft-crm", player?.fftLicence],
    queryFn: async () => (await apiRequest("GET", `/api/players/fft-crm/${player!.fftLicence}`)).json(),
    enabled: open && view === "fft" && !fft?.idCrm && !!player?.fftLicence,
  });
  const idCrm = fft?.idCrm ?? crm?.idCrm ?? null;

  // Historique tournois FFT (Résultats + Tournois de la fiche) via idCrm.
  const { data: competitions = [] } = useQuery<Competition[]>({
    queryKey: ["/api/players", idCrm, "competitions"],
    queryFn: async () => {
      const r = await (await apiRequest("GET", `/api/players/${idCrm}/competitions`)).json();
      return Array.isArray(r) ? r : (r?.competitions ?? []);
    },
    enabled: open && view === "fft" && !!idCrm,
  });
  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: ["/api/players", idCrm, "partners"],
    queryFn: async () => {
      const r = await (await apiRequest("GET", `/api/players/${idCrm}/fft-partners`)).json();
      return r?.partners ?? [];
    },
    enabled: open && view === "fft" && !!idCrm,
  });

  if (!open) return null;
  const winRate = player && player.matchesPlayed > 0 ? Math.round((player.matchesWon / player.matchesPlayed) * 100) : 0;
  const hasFft = !!player && (fft != null || player.fftRank != null || player.fftPoints != null);
  const displayName = player?.name || [fft?.prenom, fft?.nom].filter(Boolean).join(" ") || "—";
  // Joueur FFT non inscrit PadELO : pas de playerId → pas de bloc PadELO, fiche FFT seule.
  const waitingPlayer = !!playerId && (isLoading || !player);

  return (
    <div className="fixed inset-0 z-[70] bg-background overflow-y-auto font-body" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between p-4 sticky top-0 bg-background z-10">
        <button onClick={onClose} aria-label="Fermer" className="w-11 h-11 rounded-full bg-muted border border-border flex items-center justify-center text-pn-navy">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-head font-extrabold text-xl text-pn-navy">Joueur</h2>
        <span className="w-11" />
      </div>

      {waitingPlayer ? (
        <div className="p-6 space-y-4"><Skeleton className="h-56 w-full rounded-3xl" /><Skeleton className="h-28 w-full rounded-3xl" /></div>
      ) : (
        <div className="p-4 pb-10">
          <h3 className="text-center font-head font-extrabold text-2xl text-pn-navy mt-2">{displayName}</h3>

          {hasFft && (
            <div className="flex bg-pn-navy rounded-full p-1.5 gap-1 mt-4 font-head">
              {(["padelo", "fft"] as const).map((v) => (
                <button key={v} onClick={() => setView(v)}
                  className={`flex-1 py-2.5 rounded-full font-bold transition ${view === v ? "bg-white text-pn-navy" : "text-white"}`}>
                  {v === "padelo" ? "PadELO" : "FFT"}
                </button>
              ))}
            </div>
          )}

          {view === "padelo" && player ? (
            <>
              <div className="rounded-3xl bg-pn-navy text-white text-center py-6 px-4 mt-4">
                <div className="font-head font-extrabold text-5xl leading-none">{player.eloRating}</div>
                <div className="opacity-90 mt-1">Points ELO</div>
                <span className="inline-block mt-3 border border-white/40 rounded-full px-4 py-1.5 font-semibold">Niveau {tierOf(player.eloRating)} / 8</span>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mt-4">
                <Pill label="Victoires">{player.matchesWon}</Pill>
                <Pill label="Win rate">{winRate}%</Pill>
                <Pill label="Matchs">{player.matchesPlayed}</Pill>
              </div>
              <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">Évolution ELO</span></div>
              <div className="rounded-3xl border border-border p-4"><EloChart data={history} /></div>
            </>
          ) : (() => {
            const fRank = fft?.rank ?? crm?.rank ?? player?.fftRank;
            const fPoints = fft?.points ?? crm?.points ?? player?.fftPoints;
            const fEvo = fft?.evolution ?? crm?.evolution ?? player?.fftEvolution;
            const fLic = fft?.licence ?? player?.fftLicence;
            const fAgeRaw = fft?.age ?? crm?.age;
            const fAge = fAgeRaw != null ? Number(fAgeRaw) : null;
            const fVille = fft?.club || crm?.club || null;
            return (
              <>
                <div className="rounded-3xl bg-pn-navy text-white text-center py-6 px-4 mt-4">
                  <div className="font-head font-extrabold text-5xl leading-none">{fRank != null ? `#${fRank}` : "—"}</div>
                  <div className="opacity-90 mt-1">Classement France</div>
                </div>
                <div className="flex gap-2.5 mt-4">
                  {fAge != null && !isNaN(fAge) && <FftPill><User className="w-4 h-4 text-pn-navy" />{fAge} ans</FftPill>}
                  <FftPill><FlagFR /> FRA</FftPill>
                  {fPoints != null && <FftPill><TrendingUp className="w-4 h-4 text-pn-navy" />{fPoints} pts</FftPill>}
                </div>
                {(fVille || fLic) && (
                  <div className="flex gap-2.5 mt-2.5">
                    {fVille && <FftPill><MapPin className="w-4 h-4 text-pn-navy" />{fVille}</FftPill>}
                    {fLic && (
                      <button onClick={() => copyLicence(fLic)} className="flex-1 min-w-0 bg-pn-blue-soft rounded-2xl py-3.5 px-2 flex items-center justify-center gap-2 text-pn-navy font-semibold">
                        {copied ? <Check className="w-4 h-4 text-pn-green" /> : <Copy className="w-4 h-4 text-pn-blue" />}
                        <span className="truncate">{copied ? "Copié" : `#${fLic}`}</span>
                      </button>
                    )}
                  </div>
                )}
                {fEvo != null && fEvo !== 0 && (
                  <p className={`text-center mt-3 font-semibold ${fEvo > 0 ? "text-green-600" : "text-red-500"}`}>{fEvo > 0 ? `▲ ${fEvo}` : `▼ ${Math.abs(fEvo)}`} places</p>
                )}

                {competitions.length > 0 && (() => {
                  // FFT ne compte que les 12 meilleurs résultats (par points) : médailles + barres + stats basés sur eux.
                  const byPoints = [...competitions].sort((a, b) => (b.points ?? -1) - (a.points ?? -1));
                  const counted = byPoints.slice(0, 12);
                  const countedIds = new Set(counted.map((c) => c.competition_id));
                  const best = counted[0] ?? null;
                  const bestLabel = best ? `${best.rang_obtenu != null ? `#${best.rang_obtenu} ` : ""}${best.niveau ?? ""}`.trim() || "—" : "—";
                  const partnersCount = partners.length || new Set(counted.map((c) => c.partenaire).filter(Boolean)).size;

                  // Niveaux triés plus grande catégorie d'abord (P1000 avant P500 avant P250).
                  const niveaux = Array.from(new Set(counted.map((c) => c.niveau).filter(Boolean) as string[])).sort((a, b) => niveauIdx(b) - niveauIdx(a));
                  const byNiveau = niveaux.map((niv) => {
                    const comps = counted.filter((c) => c.niveau === niv);
                    const rc: Record<number, number> = {};
                    comps.forEach((c) => { if (c.rang_obtenu != null) rc[c.rang_obtenu] = (rc[c.rang_obtenu] || 0) + 1; });
                    // Seulement les 4 meilleures places (rang le plus bas) pour tenir sur une ligne.
                    const ranks = Object.entries(rc).map(([r, n]) => ({ r: +r, n })).sort((a, b) => a.r - b.r).slice(0, 4);
                    return { niv, total: comps.length, ranks };
                  });
                  const ptsByNiveau = niveaux.map((niv) => ({ niv, pts: counted.filter((c) => c.niveau === niv).reduce((s, c) => s + (c.points || 0), 0) }))
                    .sort((a, b) => niveauIdx(b.niv) - niveauIdx(a.niv));
                  const maxPts = Math.max(1, ...ptsByNiveau.map((p) => p.pts));
                  const totalPts = ptsByNiveau.reduce((s, p) => s + p.pts, 0);

                  const sortFn = (a: Competition, b: Competition) => {
                    if (tri === "points") return (b.points ?? -1) - (a.points ?? -1);
                    if (tri === "rang") return (a.rang_obtenu ?? 1e9) - (b.rang_obtenu ?? 1e9);
                    return new Date(b.expiration ?? 0).getTime() - new Date(a.expiration ?? 0).getTime();
                  };
                  const countedSorted = [...counted].sort(sortFn);
                  const uncounted = competitions.filter((c) => !countedIds.has(c.competition_id)).sort(sortFn);
                  const triLabel = { points: "points gagnés", date: "date", rang: "rang" }[tri];

                  return (
                    <>
                      {/* Partenaires */}
                      {partners.length > 0 && (
                        <>
                          <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">Partenaires</span></div>
                          <div className="flex gap-3 overflow-x-auto pt-2 pb-4 [&::-webkit-scrollbar]:hidden">
                            {partners.map((p) => (
                              <div key={p.name} className="shrink-0 w-[92px] flex flex-col items-center text-center">
                                <div className="relative">
                                  <div className="w-16 h-16 rounded-full bg-pn-blue-soft text-pn-blue font-head font-bold text-lg flex items-center justify-center">{initials(p.prenom && p.nom ? `${p.prenom} ${p.nom}` : p.name)}</div>
                                  <span className="absolute -top-1 -right-1 bg-pn-blue text-white text-xs font-bold px-1.5 py-0.5 rounded-full">x{p.n}</span>
                                  {p.rank != null && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-pn-lime text-pn-navy text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">#{Number(p.rank).toLocaleString("fr-FR").replace(/ /g, " ")}</span>}
                                </div>
                                <div className="text-xs text-pn-navy mt-3 leading-tight line-clamp-2 break-words">{p.prenom || p.name}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Résultats */}
                      <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">Résultats</span></div>
                      <div className="rounded-3xl bg-pn-navy text-white flex py-5 mb-4">
                        {[[competitions.length, "Tournois"], [bestLabel, "Meilleure perf"], [partnersCount, "Partenaires"]].map(([v, l], i) => (
                          <div key={l as string} className={`flex-1 text-center ${i ? "border-l border-white/20" : ""}`}>
                            <div className="font-head font-extrabold text-xl">{v}</div>
                            <div className="opacity-85 text-sm">{l}</div>
                          </div>
                        ))}
                      </div>

                      {/* Médailles par niveau */}
                      <div className="space-y-3">
                        {byNiveau.map((g) => (
                          <div key={g.niv} className="flex items-center gap-2.5 flex-nowrap overflow-hidden border-b border-border/60 pt-2 pb-3">
                            <span className="relative bg-pn-blue-soft text-pn-blue font-bold text-sm rounded-full px-3 py-1">
                              {g.niv}
                              <span className="absolute -top-2 -right-2 bg-pn-blue text-white text-[10px] font-bold px-1.5 rounded-full">x{g.total}</span>
                            </span>
                            {g.ranks.map(({ r, n }) => (
                              <span key={r} className="inline-flex items-center gap-1.5 font-bold text-pn-navy">
                                {n} x
                                {r <= 3
                                  ? <span style={{ background: medalBg(r) }} className="w-7 h-7 rounded-full text-white text-xs flex items-center justify-center">{r}</span>
                                  : <span className="bg-muted text-pn-navy rounded-full px-2.5 py-1 text-sm">{ordinal(r)}</span>}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>

                      {/* Points par catégorie */}
                      <div className="flex items-center justify-between mt-5 mb-3">
                        <h4 className="font-head font-bold text-pn-navy">Points par catégorie</h4>
                        <span className="text-muted-foreground font-semibold">{totalPts.toLocaleString("fr-FR").replace(/ /g, " ")} pts</span>
                      </div>
                      <div className="space-y-2.5">
                        {ptsByNiveau.map((p) => (
                          <div key={p.niv} className="flex items-center gap-3">
                            <span className="bg-pn-blue-soft text-pn-blue font-bold text-xs rounded-full px-3 py-1 min-w-[62px] text-center">{p.niv}</span>
                            <div className="flex-1 h-4 bg-pn-blue-soft rounded-full overflow-hidden">
                              <div className="h-full bg-pn-navy rounded-full" style={{ width: `${Math.max(6, (p.pts / maxPts) * 100)}%` }} />
                            </div>
                            <span className="font-bold text-pn-navy text-sm min-w-[72px] text-right">{p.pts.toLocaleString("fr-FR").replace(/ /g, " ")} pts</span>
                          </div>
                        ))}
                      </div>

                      {/* Tournois */}
                      <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">Tournois</span></div>
                      <div className="relative mb-3">
                        <button onClick={() => setTriOpen((o) => !o)} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-2.5 font-semibold text-pn-navy">
                          Tri : <b className="text-pn-blue">{triLabel}</b> <span className="text-xs">▾</span>
                        </button>
                        {triOpen && (
                          <div className="absolute z-10 mt-1 bg-background border border-border rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                            {(["points", "date", "rang"] as const).map((k) => (
                              <button key={k} onClick={() => { setTri(k); setTriOpen(false); }}
                                className={`block w-full text-left px-4 py-3 font-semibold ${k === tri ? "text-pn-blue bg-pn-blue-soft" : "text-pn-navy"}`}>
                                {{ points: "points gagnés", date: "date", rang: "rang" }[k]}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        {countedSorted.map((c) => <CompRow key={c.competition_id} c={c} onOpen={setOpenComp} />)}
                      </div>

                      {uncounted.length > 0 && (
                        <>
                          <button onClick={() => setShowUncounted((o) => !o)}
                            className="w-full mt-3 rounded-2xl border border-border py-3.5 flex items-center justify-center gap-2 font-bold text-pn-navy">
                            {showUncounted ? "Masquer les non comptabilisés" : "Tournois non comptabilisés"}
                            <ChevronDown className={`w-4 h-4 transition-transform ${showUncounted ? "rotate-180" : ""}`} />
                          </button>
                          {showUncounted && (
                            <div className="space-y-3 mt-3">
                              {uncounted.map((c) => <CompRow key={c.competition_id} c={c} onOpen={setOpenComp} />)}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}

      {openComp != null && <TournamentResultPN competitionId={openComp} onClose={() => setOpenComp(null)} />}
    </div>
  );
}

// Ligne tournoi (réutilisée pour comptabilisés + non comptabilisés).
function CompRow({ c, onOpen }: { c: Competition; onOpen: (id: number) => void }) {
  const m = monthsUntil(c.expiration);
  const soon = m != null && m <= 2;
  return (
    <button onClick={() => onOpen(c.competition_id)}
      className="w-full text-left rounded-2xl border border-border p-4 flex items-center gap-4 active:scale-[.99] transition">
      <div className="min-w-[56px]">
        {c.rang_obtenu != null && <div className="font-head font-extrabold text-2xl text-pn-navy">#{c.rang_obtenu}</div>}
        {c.points != null && <div className="mt-1.5 bg-pn-green-soft text-pn-green font-bold text-xs rounded-full px-2.5 py-0.5 text-center">+{c.points} pts</div>}
        {c.niveau && <div className="mt-1.5 bg-pn-blue-soft text-pn-blue font-bold text-xs rounded-full px-2.5 py-0.5 text-center">{c.niveau}</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-pn-navy leading-snug">{c.nom_tournoi ?? "—"}</div>
        {c.categorie && <div className="text-muted-foreground text-sm mt-0.5">{formatLabel(c.categorie)}</div>}
        <div className={`text-sm mt-1 font-medium ${soon ? "text-red-500" : "text-muted-foreground"}`}>
          {m != null && m <= 0 ? "Expiré le " : "Validité : "}{frDate(c.expiration)}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
    </button>
  );
}

function Pill({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-pn-blue-soft rounded-2xl py-3.5 px-2 text-center">
      <div className="font-head font-extrabold text-xl text-pn-navy">{children}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

// Pill style fiche FFT Padel Now : icône + valeur sur une ligne.
function FftPill({ children }: { children: ReactNode }) {
  return (
    <div className="flex-1 min-w-0 bg-pn-blue-soft rounded-2xl py-3.5 px-2 flex items-center justify-center gap-2 font-semibold text-pn-navy whitespace-nowrap overflow-hidden">
      {children}
    </div>
  );
}
